import { useCallback, useEffect, useRef, useState } from 'react';
import { hubService } from '../../services/hub.service';
import { errorText } from '../../lib/assistantView';
import {
  BUILD_TASKS,
  MAX_RENDERED,
  allowedModelsFor,
  filterCandidates,
  hasAtLeastOneSource,
  mapJobRow,
  mergeSelectedIntoUrls,
  modelForTask,
  sourcesFromSnapshot,
  textForTask,
  toBuildPayload,
  toDiscoverPayload,
  warningText,
  type InlineDoc,
  type JobRow,
  type PromptRow,
} from '../../lib/builderForm';
import {
  STATUS_TONE,
  fmtCost,
  fmtTime,
  fmtTokens,
  hasStatedResume,
  isApprovalPause,
  modelSummary,
  pauseLabel,
  statusLabel,
  typeLabel,
} from '../../lib/jobView';
import { JobPanel } from './JobPanel';

const input = 'rounded border border-gray-300 px-3 py-2 text-sm';
const btn = 'px-3 py-1.5 text-sm rounded disabled:opacity-50';

interface Discovered {
  urls: string[];
  count: number;
  strategy?: string | null;
  warningCode?: string | null;
  warning?: string | null;
}

interface Usage {
  jobs?: { total?: number; byStatus?: Record<string, number> };
  usage?: { llmCalls?: number; inputTokens?: number; outputTokens?: number; costUsd?: number };
}

/**
 * Knowledge › Builder (A2 step B): crawl a site (or pasted text) → LLM chunks → the bot's documents, through the hub
 * (`/api/knowledge/:system/:botId/*` → llm-tester). Ported from chatbot_front hub/KnowledgeBuilder.js. Builds always
 * merge: sources not mentioned are kept; "remove on next build" drops one. Discovery is free; each page of a build is
 * one LLM call — the count is shown before Build.
 */
export function BuilderPanel({ system, botId }: { system: string; botId: string }) {
  const [error, setError] = useState<string | null>(null);

  // current corpus
  const [sources, setSources] = useState<{ url: string }[] | null>(null);
  const [removeUrls, setRemoveUrls] = useState<string[]>([]);

  // discover
  const [seed, setSeed] = useState('');
  const [include, setInclude] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [found, setFound] = useState<Discovered | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [candFilter, setCandFilter] = useState('');

  // build form
  const [urls, setUrls] = useState<string[]>(['']);
  const [companyName, setCompanyName] = useState('');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<InlineDoc[]>([]);
  const [promptRows, setPromptRows] = useState<PromptRow[]>([]);
  const [prompt, setPrompt] = useState('');
  const promptTouched = useRef(false);
  const [maxChunks, setMaxChunks] = useState(50);
  const [models, setModels] = useState<Record<string, string>>({});
  const [building, setBuilding] = useState(false);

  // jobs
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [usage, setUsage] = useState<Usage | null>(null);

  const k = useCallback(
    <T,>(method: 'GET' | 'POST', rest: string, body?: unknown) => hubService.knowledge<T>(system, botId, method, rest, body),
    [system, botId],
  );

  const refreshJobs = useCallback(async () => {
    try {
      const rows = await k<Record<string, unknown>[]>('GET', 'jobs');
      setJobs(Array.isArray(rows) ? rows.map(mapJobRow) : []);
    } catch (e) {
      setError(errorText(e));
    }
  }, [k]);

  const refreshUsage = useCallback(async () => {
    try {
      setUsage(await k<Usage>('GET', 'usage?windowHours=24'));
    } catch {
      /* best effort: keep the last value */
    }
  }, [k]);

  const refreshSources = useCallback(async () => {
    try {
      const r = await k<{ entries: unknown[] } | null>('GET', 'snapshot');
      setSources(r ? sourcesFromSnapshot(r) : []);
    } catch (e) {
      setSources(null);
      setError(errorText(e));
    }
  }, [k]);

  useEffect(() => {
    void refreshJobs();
    void refreshUsage();
    void refreshSources();
    // The chunking prompt starts as what the bot runs today (its override, else the default); editing it here applies
    // to this one build only. The rows also feed the model pickers.
    k<PromptRow[]>('GET', 'prompts')
      .then((rows) => {
        if (!Array.isArray(rows)) return;
        setPromptRows(rows);
        if (!promptTouched.current) setPrompt(textForTask(rows, 'chunk'));
      })
      .catch(() => undefined);
  }, [k, refreshJobs, refreshUsage, refreshSources]);

  // Poll while a job is live or paused, only when the tab is visible.
  const live = jobs.some((j) => j.isActive || j.isPaused);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshJobs();
        void refreshUsage();
      }
    }, 5000);
    return () => clearInterval(t);
  }, [live, refreshJobs, refreshUsage]);

  async function discover() {
    if (!seed.trim()) return;
    setDiscovering(true);
    setFound(null);
    setPicked([]);
    setCandFilter('');
    setError(null);
    try {
      const r = await k<Discovered>('POST', 'discover', toDiscoverPayload({ url: seed, includePaths: include }));
      setFound(r);
      setPicked(Array.isArray(r?.urls) ? r.urls : []);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setDiscovering(false);
    }
  }

  const [reading, setReading] = useState(false);

  /** TXT / MD are read here; a PDF's text comes from the hub's document parser (no PDF library in the browser). */
  async function onFiles(list: FileList | null) {
    const out: InlineDoc[] = [];
    const problems: string[] = [];
    setReading(true);
    try {
      for (const f of Array.from(list ?? [])) {
        try {
          if (/\.pdf$/i.test(f.name)) {
            if (f.size > 20 * 1024 * 1024) {
              problems.push(`${f.name}: larger than 20 MB`);
              continue;
            }
            out.push(await hubService.knowledgeExtract(system, botId, f));
          } else if (/\.(txt|md|markdown)$/i.test(f.name)) {
            const content = (await f.text()).trim();
            if (content) out.push({ name: f.name, content });
          } else {
            problems.push(`${f.name}: only PDF, TXT or MD`);
          }
        } catch (e) {
          problems.push(`${f.name}: ${errorText(e)}`);
        }
      }
    } finally {
      setReading(false);
    }
    setFiles(out);
    setError(problems.length ? problems.join(' · ') : null);
  }

  const canBuild = hasAtLeastOneSource({ text, files, urls, removeUrls });
  const pageCount = urls.filter((u) => u.trim()).length;

  async function build() {
    setBuilding(true);
    setError(null);
    try {
      const r = await k<{ jobId?: string }>('POST', 'build', toBuildPayload({ companyName, urls, text, files, prompt, effectivePrompt: textForTask(promptRows, 'chunk'), maxChunks, models, removeUrls }));
      if (r?.jobId) {
        setRemoveUrls([]);
        setExpanded((e) => ({ ...e, [r.jobId!]: true }));
      }
      await refreshJobs();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBuilding(false);
    }
  }

  async function jobAction(job: JobRow, action: 'cancel' | 'resume' | 'approve-shrink') {
    setBusy((b) => ({ ...b, [job.id]: true }));
    try {
      await k('POST', `jobs/${encodeURIComponent(job.id)}/${action}`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy((b) => ({ ...b, [job.id]: false }));
      await refreshJobs();
      await refreshUsage();
    }
  }

  const candidates = found ? filterCandidates(found.urls, candFilter) : [];

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-6">
      <div>
        <h2 className="font-medium text-gray-900">Builder</h2>
        <p className="text-sm text-gray-600">
          Crawl pages (or paste text), split them into chunks with the LLM, and save them as this bot’s <b>Auto</b> documents. A build
          adds to what the bot has; pages you do not mention are kept.
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2 flex justify-between gap-2">
          <span>{error}</span>
          <button type="button" className="text-gray-600 hover:underline shrink-0" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* current sources */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-800">Pages the bot has now</h3>
        {sources === null ? (
          <p className="text-sm text-gray-500">Could not load.</p>
        ) : sources.length === 0 ? (
          <p className="text-sm text-gray-500">None yet — the first build creates them.</p>
        ) : (
          <ul className="text-sm max-h-48 overflow-auto border rounded divide-y">
            {sources.map((s) => {
              const marked = removeUrls.includes(s.url);
              return (
                <li key={s.url} className="flex items-center justify-between gap-2 px-2 py-1">
                  <span className={`truncate ${marked ? 'line-through text-gray-400' : ''}`} title={s.url}>
                    {s.url}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline shrink-0"
                    onClick={() => setRemoveUrls((r) => (marked ? r.filter((u) => u !== s.url) : [...r, s.url]))}
                  >
                    {marked ? 'Keep' : 'Remove on next build'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* discover */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-800">Find pages on a site</h3>
        <div className="flex flex-wrap gap-2">
          <input className={`${input} flex-1 min-w-[16rem]`} placeholder="https://example.mn" value={seed} onChange={(e) => setSeed(e.target.value)} />
          <input className={`${input} w-56`} placeholder="Only paths (e.g. /faq, /help)" value={include} onChange={(e) => setInclude(e.target.value)} />
          <button type="button" className={`${btn} bg-gray-800 text-white`} disabled={discovering || !seed.trim()} onClick={() => void discover()}>
            {discovering ? 'Finding…' : 'Find pages'}
          </button>
        </div>
        <p className="text-xs text-gray-500">Reads the site’s sitemap only — nothing is crawled or spent yet.</p>
        {found && (
          <div className="border rounded p-2 space-y-2">
            <div className="text-sm">
              {found.count} page(s){found.strategy ? ` via ${found.strategy}` : ''}.{' '}
              {warningText(found.warningCode, found.count, found.warning) && (
                <span className="text-amber-700">{warningText(found.warningCode, found.count, found.warning)}</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <input className={`${input} flex-1`} placeholder="Filter" value={candFilter} onChange={(e) => setCandFilter(e.target.value)} />
              <button type="button" className="text-blue-600 hover:underline" onClick={() => setPicked((p) => [...new Set([...p, ...candidates])])}>
                Select shown
              </button>
              <button type="button" className="text-blue-600 hover:underline" onClick={() => setPicked((p) => p.filter((u) => !candidates.includes(u)))}>
                Clear shown
              </button>
              <span className="text-gray-500">{picked.length} selected</span>
            </div>
            <ul className="max-h-60 overflow-auto text-xs space-y-0.5">
              {candidates.slice(0, MAX_RENDERED).map((u) => (
                <li key={u}>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={picked.includes(u)}
                      onChange={(e) => setPicked((p) => (e.target.checked ? [...p, u] : p.filter((x) => x !== u)))}
                    />
                    <span className="truncate">{u}</span>
                  </label>
                </li>
              ))}
            </ul>
            {candidates.length > MAX_RENDERED && <p className="text-xs text-gray-500">Showing {MAX_RENDERED} of {candidates.length} — filter to see the rest.</p>}
            <button
              type="button"
              className={`${btn} bg-blue-600 text-white`}
              disabled={!picked.length}
              onClick={() => {
                setUrls((prev) => mergeSelectedIntoUrls(prev, picked));
                setFound(null);
                setPicked([]);
              }}
            >
              Add {picked.length} page(s) to the build
            </button>
          </div>
        )}
      </div>

      {/* build */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-800">Build</h3>
        <div className="space-y-1">
          {urls.map((u, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={`${input} flex-1`}
                placeholder="https://example.mn/page"
                value={u}
                onChange={(e) => setUrls((a) => a.map((x, j) => (j === i ? e.target.value : x)))}
              />
              <button type="button" className="text-sm text-gray-500 hover:text-red-600" onClick={() => setUrls((a) => (a.length === 1 ? [''] : a.filter((_, j) => j !== i)))}>
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => setUrls((a) => [...a, ''])}>
            + Add a page
          </button>
        </div>
        <input className={`w-full ${input}`} placeholder="Company name (helps the chunker; optional)" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        <textarea className={`w-full ${input}`} rows={4} placeholder="Or paste text (optional)" value={text} onChange={(e) => setText(e.target.value)} />
        <div className="text-sm">
          <input type="file" multiple accept=".pdf,.txt,.md,.markdown" onChange={(e) => void onFiles(e.target.files)} />
          <span className="text-xs text-gray-500 ml-2">
            {reading ? 'Reading…' : files.length ? `${files.length} file(s) read: ${files.map((f) => f.name).join(', ')}` : 'PDF, TXT or MD — their text goes into this build.'}
          </span>
        </div>

        <details className="border rounded p-2">
          <summary className="cursor-pointer text-sm">Options for this build</summary>
          <div className="space-y-2 mt-2">
            <label className="block text-sm">
              Chunking prompt <span className="text-xs text-gray-500">(this build only — the bot’s saved prompt is on the Prompts tab)</span>
              <textarea
                className={`w-full ${input} font-mono text-xs`}
                rows={8}
                value={prompt}
                onChange={(e) => {
                  promptTouched.current = true;
                  setPrompt(e.target.value);
                }}
              />
            </label>
            <label className="text-sm flex items-center gap-2">
              Max chunks per window
              <input
                type="number"
                min={1}
                max={200}
                className={`${input} w-24`}
                value={maxChunks}
                onChange={(e) => setMaxChunks(Math.max(1, Math.min(200, Number(e.target.value) || 50)))}
              />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {BUILD_TASKS.map((t) => {
                const allowed = allowedModelsFor(promptRows, t);
                return (
                  <label key={t} className="text-sm flex items-center gap-2">
                    <span className="w-16">{t}</span>
                    <select className={`${input} flex-1`} value={models[t] ?? ''} onChange={(e) => setModels((m) => ({ ...m, [t]: e.target.value }))}>
                      <option value="">default ({modelForTask(promptRows, t) || '—'})</option>
                      {allowed.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
          </div>
        </details>

        <div className="flex items-center gap-3">
          <button type="button" className={`${btn} bg-blue-600 text-white`} disabled={!canBuild || building || reading} onClick={() => void build()}>
            {building ? 'Starting…' : 'Build'}
          </button>
          <span className="text-xs text-gray-500">
            {pageCount ? `${pageCount} page(s) = about ${pageCount} LLM call(s) to collect, then chunking.` : ''}
            {removeUrls.length ? ` Removes ${removeUrls.length} page(s).` : ''}
          </span>
        </div>
      </div>

      {/* jobs */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-800">Jobs</h3>
          {usage?.usage && (
            <span className="text-xs text-gray-500">
              Last 24 h: {usage.jobs?.total ?? 0} job(s) · {fmtTokens((usage.usage.inputTokens ?? 0) + (usage.usage.outputTokens ?? 0))} tokens ·{' '}
              {fmtCost(usage.usage.costUsd)}
            </span>
          )}
        </div>
        {jobs.length === 0 ? (
          <p className="text-sm text-gray-500">No jobs yet.</p>
        ) : (
          <ul className="space-y-2">
            {jobs.map((j) => {
              const approval = j.isPaused && isApprovalPause(j.pausedReason);
              const prior = (j.pausedData as { priorCount?: number; newCount?: number } | null) ?? null;
              const u = (j.usage ?? {}) as { inputTokens?: number; outputTokens?: number; costUsd?: number };
              return (
                <li key={j.id} className="border rounded p-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" className="text-gray-500" onClick={() => setExpanded((e) => ({ ...e, [j.id]: !e[j.id] }))}>
                      {expanded[j.id] ? '▾' : '▸'}
                    </button>
                    <span className="text-gray-500 text-xs">{fmtTime(j.createdAt)}</span>
                    <span>{typeLabel(j.type)}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_TONE[j.status] ?? 'bg-gray-100 text-gray-700'}`}>{statusLabel(j.status)}</span>
                    {j.isPaused && <span className="text-xs text-amber-700">{pauseLabel(j.pausedReason)}</span>}
                    {j.isPaused && hasStatedResume(j.pausedReason) && j.resumeAt && <span className="text-xs text-gray-500">resumes {fmtTime(j.resumeAt)}</span>}
                    <span className="text-xs text-gray-500 ml-auto">
                      {fmtTokens((u.inputTokens ?? 0) + (u.outputTokens ?? 0))} tok · {fmtCost(u.costUsd)}
                      {j.models.length ? ` · ${modelSummary(j.models)}` : ''}
                    </span>
                    {j.cancellable && (
                      <button type="button" className="text-xs text-red-600 hover:underline" disabled={busy[j.id] || j.cancelRequested} onClick={() => void jobAction(j, 'cancel')}>
                        {j.cancelRequested ? 'Cancelling…' : 'Cancel'}
                      </button>
                    )}
                    {j.isPaused && !approval && (
                      <button type="button" className="text-xs text-blue-600 hover:underline" disabled={busy[j.id]} onClick={() => void jobAction(j, 'resume')}>
                        Resume now
                      </button>
                    )}
                    {approval && (
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline"
                        disabled={busy[j.id]}
                        onClick={() => {
                          const msg = prior?.priorCount != null ? `The bot has ${prior.priorCount} chunks; this build makes ${prior.newCount}. Replace them?` : 'Replace the bot’s chunks with fewer?';
                          if (window.confirm(msg)) void jobAction(j, 'approve-shrink');
                        }}
                      >
                        Approve fewer chunks
                      </button>
                    )}
                  </div>
                  {j.error && !j.isActive && <p className="text-xs text-red-600 mt-1">{j.error}</p>}
                  {expanded[j.id] && <JobPanel key={j.id} system={system} botId={botId} jobId={j.id} jobStatus={j.status} />}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
